import { gql, useMutation } from "@apollo/client";
import {
  InviteCompanyUserInput,
  Organization_State,
  Type_Of_Organization,
  useCreateOrUpdateCompanyMutation,
} from "@generated/graphql";
import { Box, Button, Stack } from "@mui/material";
import Form from "components/form/Form";
import { ORGANIZATION_STATES, ORGANIZATION_TYPES } from "constants/company";
import ROUTES from "constants/routes";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { UserContext } from "context/userContext";
import dayjs from "dayjs";
import { setAuthCookies } from "helpers/cookieHelper";
import { parseApolloError } from "helpers/errorsParser";
import { cleanSpaceInString } from "helpers/validation/createValidation";
import useDaData, { DaDataResponse, DaDataSuggestion } from "hooks/useDaData";
import TEAM_ROLE_OPTIONS from "pages/Company/components/TeamTab/constants";
import { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import CompanyDetailsStep from "./components/CompanyDetailsStep";
import ManagerStep from "./components/ManagerStep";
import CompanyRegisterStepper from "./components/Stepper";
import TeamStep from "./components/TeamStep";
import { DEFAULT_COMPANY_REGISTER_VALUES, FALLBACK_MANAGER_POSITION, FALLBACK_USER_POSITION } from "./constants";
import { CompanyRegisterFormValues, InnLookupState, TeamMemberDraft } from "./types";

const INVITE_COMPANY_USERS_MUTATION = gql`
  mutation inviteCompanyUsers($input: [InviteCompanyUserInput!]!) {
    inviteCompanyUsers(input: $input) {
      id
    }
  }
`;

type InviteCompanyUsersMutationResult = {
  inviteCompanyUsers: Array<{
    id: string;
  }>;
};

type InviteCompanyUsersMutationVariables = {
  input: InviteCompanyUserInput[];
};

const CompanyRegister = () => {
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const { meRefetch, userCompaniesRefetch } = useContext(UserContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [innLookupState, setInnLookupState] = useState<InnLookupState>("idle");
  const [lastSearchedInn, setLastSearchedInn] = useState("");
  const { getDaData, loading: isInnLookupLoading } = useDaData();

  const form = useForm<CompanyRegisterFormValues>({
    defaultValues: DEFAULT_COMPANY_REGISTER_VALUES,
  });
  const {
    getValues,
    reset,
    trigger,
    watch,
    formState: { isSubmitting },
  } = form;
  const watchedInn = watch("inn");
  const isCompanyLeader = watch("isCompanyLeader");

  const [createCompany, { loading: isCreateCompanyLoading }] = useCreateOrUpdateCompanyMutation();
  const [inviteCompanyUsers, { loading: isInviteCompanyUsersLoading }] = useMutation<
    InviteCompanyUsersMutationResult,
    InviteCompanyUsersMutationVariables
  >(INVITE_COMPANY_USERS_MUTATION);

  useEffect(() => {
    if (watchedInn !== lastSearchedInn && innLookupState !== "idle") {
      setInnLookupState("idle");
    }
  }, [innLookupState, lastSearchedInn, watchedInn]);

  const roleLabelMap = useMemo(
    () =>
      TEAM_ROLE_OPTIONS.reduce<Record<string, string>>((acc, role) => {
        acc[role.value] = role.label;
        return acc;
      }, {}),
    []
  );

  const applyDaDataSuggestion = (company: DaDataSuggestion) => {
    const currentValues = getValues();
    const {
      inn,
      kpp,
      ogrn,
      okpo,
      okato,
      oktmo,
      address,
      ogrn_date: ogrnDate,
      name,
      type,
      state,
      management,
    } = company.data;

    reset({
      ...currentValues,
      inn: inn || currentValues.inn,
      fullName: name.full_with_opf || currentValues.fullName,
      shortName: name.short_with_opf || currentValues.shortName,
      kpp: kpp || currentValues.kpp,
      ogrn: ogrn || currentValues.ogrn,
      okpo: okpo || currentValues.okpo,
      okato: okato || currentValues.okato,
      oktmo: oktmo || currentValues.oktmo,
      registrationAddress: address.value || currentValues.registrationAddress,
      ogrnDate: ogrnDate ? dayjs(ogrnDate) : currentValues.ogrnDate,
      registrationDate: state.registration_date ? dayjs(state.registration_date) : currentValues.registrationDate,
      type: ORGANIZATION_TYPES.find((organizationType) => organizationType.id === type) || currentValues.type,
      state:
        ORGANIZATION_STATES.find((organizationState) => organizationState.id === state.status) || currentValues.state,
      managerFio: management?.name || currentValues.managerFio,
      managerPosition: management?.post || currentValues.managerPosition,
    });
  };

  const handleLookupByInn = async () => {
    const isValid = await trigger("inn");

    if (!isValid) return;

    const normalizedInn = cleanSpaceInString(getValues("inn"));
    let suggestions: DaDataResponse["suggestions"] | undefined;

    await getDaData({ query: normalizedInn }, (value) => {
      if (typeof value === "function") {
        suggestions = value(suggestions);
        return;
      }

      suggestions = value;
    });

    setLastSearchedInn(normalizedInn);

    const matchedCompany = suggestions?.find((suggestion) => suggestion.data.inn === normalizedInn) || suggestions?.[0];

    if (!matchedCompany) {
      setInnLookupState("not-found");
      addToast({ type: ToastTypeEnum.ERROR, text: "Компания по ИНН не найдена" });
      return;
    }

    applyDaDataSuggestion(matchedCompany);
    setInnLookupState("success");
    addToast({ type: ToastTypeEnum.SUCCESS, text: "Данные по ИНН найдены" });
  };

  const handleNextStep = async () => {
    if (currentStep === 1) {
      const isValid = await trigger(["inn", "fullName", "shortName", "kpp", "ogrn", "okpo"]);

      if (!isValid) {
        return;
      }
    }

    if (currentStep === 2) {
      const fieldsToValidate = isCompanyLeader ? ["leaderFullName"] : ["leaderFullName", "userPosition", "managerFio"];
      const isValid = await trigger(fieldsToValidate as Array<keyof CompanyRegisterFormValues>);

      if (!isValid) {
        addToast({ type: ToastTypeEnum.ERROR, text: "Проверьте данные руководителя" });
        return;
      }
    }

    setCurrentStep((prevState) => Math.min(prevState + 1, 3));
  };

  const handlePreviousStep = () => {
    setCurrentStep((prevState) => Math.max(prevState - 1, 1));
  };

  const inviteTeamMembers = (members: TeamMemberDraft[]) =>
    inviteCompanyUsers({
      variables: {
        input: members.map((member) => ({
          email: cleanSpaceInString(member.email),
          position: cleanSpaceInString(member.position),
          role: member.role,
        })),
      },
    }).then(() => undefined);

  const submitCompany = async (values: CompanyRegisterFormValues) => {
    const normalizedLeaderFullName = cleanSpaceInString(values.leaderFullName);
    const normalizedUserPosition = values.isCompanyLeader
      ? FALLBACK_USER_POSITION
      : cleanSpaceInString(values.userPosition);
    const normalizedManagerFio = values.isCompanyLeader
      ? normalizedLeaderFullName
      : cleanSpaceInString(values.managerFio);
    const normalizedManagerPosition = cleanSpaceInString(values.managerPosition) || FALLBACK_MANAGER_POSITION;
    const registrationDate = values.registrationDate || dayjs();
    const ogrnDate = values.ogrnDate || dayjs();

    try {
      const response = await createCompany({
        variables: {
          input: {
            managerPosition: normalizedManagerPosition,
            managerFio: normalizedManagerFio,
            shortName: cleanSpaceInString(values.shortName),
            fullName: cleanSpaceInString(values.fullName),
            inn: cleanSpaceInString(values.inn),
            type: values.type?.id || Type_Of_Organization.Legal,
            userPosition: normalizedUserPosition,
            ogrnDate: ogrnDate.toDate().getTime(),
            ogrn: cleanSpaceInString(values.ogrn),
            kpp: cleanSpaceInString(values.kpp),
            okato: cleanSpaceInString(values.okato || ""),
            okpo: cleanSpaceInString(values.okpo || ""),
            oktmo: cleanSpaceInString(values.oktmo || ""),
            registrationDate: registrationDate.toDate().getTime(),
            state: values.state?.id || Organization_State.Active,
            registrationAddress: cleanSpaceInString(values.registrationAddress || ""),
          },
        },
      });

      const companyId = response.data?.createOrUpdateCompany.id;

      if (!companyId) {
        addToast({ type: ToastTypeEnum.ERROR, text: "Не удалось создать компанию" });
        return;
      }

      setAuthCookies({ name: "companyId", value: companyId });

      if (values.teamMembers.length > 0) {
        try {
          await inviteTeamMembers(values.teamMembers);
        } catch (error) {
          parseApolloError(error, {
            addToast,
            defaultText: "Не удалось отправить приглашения сотрудникам",
          });
        }
      }

      addToast({ type: ToastTypeEnum.SUCCESS, text: "Компания создана" });
      await Promise.allSettled([meRefetch(), userCompaniesRefetch()]);
      navigate(`/${ROUTES.COMPETENCE_MAPS}`);
    } catch (error) {
      parseApolloError(error, { addToast });
    }
  };

  const handleCreateCompany = () => submitCompany(getValues());
  const handleNavigateBackToCabinet = () => navigate(`/${ROUTES.COMPETENCE_MAPS}`);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 664,
      }}>
      <Form form={form}>
        <Stack spacing={4}>
          <CompanyRegisterStepper currentStep={currentStep} />

          {currentStep === 1 && (
            <CompanyDetailsStep
              innLookupState={innLookupState}
              isInnLookupLoading={isInnLookupLoading}
              onLookupByInn={handleLookupByInn}
              onNextStep={handleNextStep}
            />
          )}

          {currentStep === 2 && (
            <ManagerStep
              isCompanyLeader={isCompanyLeader}
              isSubmitting={isSubmitting}
              onNextStep={handleNextStep}
              onPreviousStep={handlePreviousStep}
            />
          )}

          {currentStep === 3 && (
            <TeamStep
              isCreateCompanyLoading={isCreateCompanyLoading || isInviteCompanyUsersLoading}
              onCreateCompany={handleCreateCompany}
              onPreviousStep={handlePreviousStep}
              roleLabelMap={roleLabelMap}
            />
          )}

          <Button
            type='button'
            variant='text'
            size='small'
            onClick={handleNavigateBackToCabinet}
            sx={{
              alignSelf: "flex-start",
              px: 0,
              color: "primary.main",
              fontSize: 16,
              fontWeight: 600,
              textTransform: "none",
            }}>
            Вернуться в личный кабинет
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

export default CompanyRegister;
